import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Alert, Box, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import CloseIcon from '@mui/icons-material/Close';
import JBrowseService from '../services/jbrowse.service';
import _ from 'underscore';
import dayjs from 'dayjs';

function EditNotesModal(props) {
    const {
        currentNote,
        editNotesModalIsOpen,
        setEditNotesModalIsOpen,
        selectedAssembly,
        currentUser,
        setNotesIsOpen,
        setLoading,
        setNotes,
    } = props;

    const [editedNote, setEditedNote] = useState({
        category: currentNote ? currentNote.category : '',
        chromosome: currentNote ? currentNote.chromosome : '',
        start: currentNote ? currentNote.start : '',
        end: currentNote ? currentNote.end : '',
        class: currentNote ? (currentNote.Public === 0 ? 'Public' : 'Private') : '',
        note: currentNote ? currentNote.note : ''
    });

    const [alert, setAlert] = useState({ type: '', message: '' });

    useEffect(() => {
        setEditedNote(currentNote);
    }, [currentNote]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedNote(prevState => ({ ...prevState, [name]: value }));
    };

    const saveEdit = () => {
        setLoading(true);
        const obj = {
            old: currentNote,
            new: editedNote,
            reference: selectedAssembly,
            user: currentUser
        };

        JBrowseService.editNotes(obj).then(
            (response) => {
                const notesObj = {
                    idCompany: currentUser.Company_idCompany,
                    username: currentUser.email,
                    assembly: selectedAssembly,
                };
                JBrowseService.getNotes(notesObj).then(
                    (notesResponse) => {
                        const fetchedNotes = [];
                        _.each(notesResponse.result, (value) => {
                            const noteObj = value;
                            noteObj.created_at = dayjs(value.created_at).format('MMMM D YYYY, h:mm:ss A');
                            fetchedNotes.push(noteObj);
                        });
                        setNotes(fetchedNotes);
                    },
                    (error) => {
                        console.log('error: ', error);
                    }
                );
                setAlert({ type: 'success', message: 'Note was saved successfully.' });
                setTimeout(() => {
                    setAlert('');
                }, 3000);
                setLoading(false);
            },
            (error) => {
                console.log('error: ', error);
                setAlert({ type: 'error', message: 'Something went wrong, refresh your browser and try again.' });
                setTimeout(() => {
                    setAlert('');
                }, 3000);
                setLoading(false);
            }
        );
    };

    const closeModal = () => {
        setEditNotesModalIsOpen(false);
    };

    const onGoBack = () => {
        setEditNotesModalIsOpen(false);
        setTimeout(() => {
            setNotesIsOpen(true);
        });
    };

    return (
        <Dialog
            open={editNotesModalIsOpen}
            onClose={closeModal}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                    borderRadius: 2
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Notes</Typography>
                <IconButton onClick={closeModal}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box component="form" sx={{ mt: 2 }}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="category-label">Type</InputLabel>
                        <Select
                            labelId="category-label"
                            label="Type"
                            name="category"
                            value={editedNote?.category || ''}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="" disabled>Please select</MenuItem>
                            <MenuItem value="Gene">Gene</MenuItem>
                            <MenuItem value="Variant">Variant</MenuItem>
                            <MenuItem value="Primer">Primer</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Chromosome"
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 2 }}
                        name="chromosome"
                        value={editedNote?.chromosome || ''}
                        onChange={handleInputChange}
                    />

                    <TextField
                        label="Starting"
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 2 }}
                        name="start"
                        type="number"
                        value={editedNote?.start || ''}
                        onChange={handleInputChange}
                    />

                    <TextField
                        label="Ending"
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 2 }}
                        name="end"
                        type="number"
                        value={editedNote?.end || ''}
                        onChange={handleInputChange}
                    />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="class-label">Class</InputLabel>
                        <Select
                            labelId="class-label"
                            label="Class"
                            name="class"
                            value={editedNote?.class || ''}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="" disabled>Please select</MenuItem>
                            <MenuItem value="Public">Public (Company)</MenuItem>
                            <MenuItem value="Private">Private (User)</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Notes"
                        multiline
                        rows={4}
                        variant="outlined"
                        fullWidth
                        name="note"
                        value={editedNote?.note || ''}
                        onChange={handleInputChange}
                    />
                </Box>

                {alert.type === 'success' && (
                    <Alert
                        severity="success"
                        icon={<FontAwesomeIcon icon={faCheckCircle} />}
                        sx={{ mt: 2, textAlign: 'center' }}
                    >
                        {alert.message}
                    </Alert>
                )}

                {alert.type === 'error' && (
                    <Alert
                        severity="error"
                        icon={<FontAwesomeIcon icon={faExclamationCircle} />}
                        sx={{ mt: 2, textAlign: 'center' }}
                    >
                        {alert.message}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={onGoBack}
                    variant="contained"
                >
                    <i className="fas fa-hand-point-left" style={{ marginRight: '8px' }}></i> Go Back
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    onClick={saveEdit}
                    variant="contained"
                >
                    <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} /> Save Note
                </Button>
                <Button variant="outlined" onClick={closeModal}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditNotesModal;
